import React, { useState, useEffect } from 'react';
import {
  TextInput,
  Form,
  Button,
  InlineLoading
} from 'carbon-components-react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FormError } from 'components';
interface FormValues {
  emailDomains: string;
}

const Schema = Yup.object().shape({
  emailDomains: Yup.string()
    .label('Email domain')
    .required()
    .test(
      'validate-domain',
      'Please enter valid domain',
      function (value: any) {
        const failedValidation = [];
        if (value) {
          const regex = new RegExp(
            '^((?!-)[a-zA-Z0-9-]{1,63}(?<!-)\\.)+[A-Za-z]{2,9}$'
          );
          const domainArr = value.split(',');
          domainArr.forEach((ele: string) => {
            if (regex.test(ele.trim()) === false) {
              failedValidation.push(ele);
            }
          });
          if (failedValidation.length > 0) {
            return false;
          }
        }
        return true;
      }
    )
});

const InternalUsers: React.FC<Props> = ({
  organisation,
  handleEmailDomainSubmit,
  configurationUpdateType,
  isUpdatingConfiguration
}) => {
  const email_domains = organisation?.email_domains;

  const [localSavingEmailDomain, setLocalSavingEmailDomain] =
    useState<boolean>(false);
  const [isFieldError, setIsFieldError] = useState<boolean>(false);

  const initialValues = {
    emailDomains: email_domains?.join(', ')
  };

  const {
    errors,
    values,
    touched,
    setFieldValue,
    handleSubmit,
    setFieldTouched
  } = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: Schema,
    onSubmit: (values: FormValues) => {
      setLocalSavingEmailDomain(true);
      if (values.emailDomains?.trim()) {
        const domainArr: string[] = values.emailDomains
          .split(',')
          .map((item: string) => item.trim());
        if (domainArr.length > 0) {
          handleEmailDomainSubmit({ email_domains: domainArr });
        }
      }
    }
  });

  const handleTextInputChange = (val: string) => {
    setFieldValue('emailDomains', val, true);
    setFieldTouched('emailDomains', true, false);
  };

  useEffect(() => {
    if (Object.keys(errors).length <= 0 && values.emailDomains) {
      setIsFieldError(false);
    } else {
      setIsFieldError(true);
    }
  }, [errors, values]);

  useEffect(() => {
    if (isUpdatingConfiguration && configurationUpdateType === 'emailDomains') {
      setLocalSavingEmailDomain(true);
    } else if (
      !isUpdatingConfiguration &&
      configurationUpdateType === 'emailDomains'
    ) {
      setTimeout(() => {
        setLocalSavingEmailDomain(false);
      }, 2000);
    }
  }, [organisation, isUpdatingConfiguration, configurationUpdateType]);

  return (
    <Form id="emailDomainForm" onSubmit={handleSubmit}>
      <div id="internalUsers" className="internal-users spacing-padding">
        <div className="sub-section-title">Internal users</div>
        <div className="section-description">
          Users with these email domains will be treated as internal and be part
          of your organisation
        </div>
        <div className="email-domains-textinput-container">
          <TextInput
            id="autoRenewUntil"
            name="autoRenewUntil"
            className="textinput-custom-style"
            labelText="Email domains"
            helperText="Multiple additional domains can be separated by commas"
            onChange={(val: any) => handleTextInputChange(val.target.value)}
            value={values.emailDomains}
          />
          {errors.emailDomains && touched.emailDomains && (
            <FormError>{errors.emailDomains}</FormError>
          )}
        </div>
        <div className="button-container">
          <Button
            id="ssoRoleMappingButton"
            type="submit"
            kind="tertiary"
            size="default"
            className="button-size"
            disabled={
              (isUpdatingConfiguration &&
                configurationUpdateType === 'emailDomains') ||
              localSavingEmailDomain ||
              isFieldError
            }
          >
            {!(
              isUpdatingConfiguration &&
              configurationUpdateType === 'emailDomains'
            ) && !localSavingEmailDomain ? (
              'Save domains'
            ) : (
              <InlineLoading
                description={
                  !(
                    isUpdatingConfiguration &&
                    configurationUpdateType === 'emailDomains'
                  )
                    ? 'Domains saved'
                    : 'Saving...'
                }
                status={
                  !(
                    isUpdatingConfiguration &&
                    configurationUpdateType === 'emailDomains'
                  )
                    ? 'finished'
                    : 'active'
                }
                aria-live={'assertive'}
              />
            )}
          </Button>
        </div>
        <div id="gisViewersAnchor"></div>
      </div>
    </Form>
  );
};

interface Props {
  organisation: any;
  handleEmailDomainSubmit: Function;
  configurationUpdateType: string;
  isUpdatingConfiguration: boolean;
}

export default React.memo(InternalUsers);
